/**
 * SiteSubmissions page - Dashboard for viewing form submissions
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { sitesApi } from '../../api/client';
import { useSubmissionsState } from './useSubmissionsState';
import { useSubmissionsHandlers } from './useSubmissionsHandlers';
import { SubmissionsHeader } from './SubmissionsHeader';
import { SubmissionsFilters } from './SubmissionsFilters';
import { SubmissionsTable } from './SubmissionsTable';
import { SubmissionsPagination } from './SubmissionsPagination';
import { SubmissionDetailModal } from './SubmissionDetailModal';
import { Toast } from '../../components/Toast';

export const SiteSubmissions: React.FC = () => {
  const [siteName, setSiteName] = useState<string>('');
  const [siteLoading, setSiteLoading] = useState(true);
  const [siteError, setSiteError] = useState<string | null>(null);

  const {
    siteId,
    submissions,
    stats,
    isLoading,
    error,
    selectedSubmission,
    pagination,
    filters,
    loadSubmissions,
    handlePageChange,
    setUnreadOnly,
    setDateRangeDays,
    setSearch,
    setSelectedSubmission,
    updateSubmissionInList,
    removeSubmissionFromList,
  } = useSubmissionsState();

  const { isDeleting, toast, handleDelete, handleViewSubmission, dismissToast } =
    useSubmissionsHandlers({
      siteId,
      updateSubmissionInList,
      removeSubmissionFromList,
      setSelectedSubmission,
      loadSubmissions,
    });

  // Load site info for the header
  useEffect(() => {
    const loadSite = async () => {
      if (!siteId) return;

      try {
        const allSites = await sitesApi.list();
        const site = allSites.find(s => s.id === siteId);
        if (site) {
          setSiteName(site.name);
        } else {
          setSiteError('Site not found');
        }
      } catch (err) {
        console.error('Failed to load site:', err);
        setSiteError('Failed to load site information');
      } finally {
        setSiteLoading(false);
      }
    };

    loadSite();
  }, [siteId]);

  // Error state
  if (siteError || (!siteLoading && !siteName)) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <h2 className="text-2xl font-bold text-gray-900">Site not found</h2>
          <p className="mt-2 text-gray-600">
            The site you're looking for doesn't exist or you don't have access to it.
          </p>
          <Link to="/dashboard" className="mt-4 btn-primary inline-block">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // Loading state
  if (siteLoading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 mx-auto mb-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <SubmissionsHeader
        siteName={siteName}
        siteId={siteId!}
        stats={stats}
        isLoading={isLoading}
        onRefresh={() => loadSubmissions()}
      />

      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
            <button onClick={() => loadSubmissions()} className="ml-4 underline hover:no-underline">
              Try again
            </button>
          </div>
        ) : (
          <>
            <SubmissionsFilters
              unreadOnly={filters.unreadOnly}
              dateRangeDays={filters.dateRangeDays}
              search={filters.search}
              onUnreadOnlyChange={setUnreadOnly}
              onDateRangeChange={setDateRangeDays}
              onSearchChange={setSearch}
            />

            <SubmissionsTable
              submissions={submissions}
              isLoading={isLoading}
              onView={handleViewSubmission}
              onDelete={handleDelete}
              isDeleting={isDeleting}
            />

            <SubmissionsPagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </main>

      <SubmissionDetailModal
        submission={selectedSubmission}
        isOpen={!!selectedSubmission}
        onClose={() => setSelectedSubmission(null)}
        onDelete={handleDelete}
        isDeleting={isDeleting}
      />

      <Toast
        message={toast?.message || ''}
        type={toast?.type || 'info'}
        isVisible={!!toast}
        onClose={dismissToast}
      />
    </div>
  );
};

export default SiteSubmissions;
